/** Ponto de encontro e avisos rápidos — cliente. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export interface Encontro {
  id: string;
  title: string;
  latitude: number;
  longitude: number;
  at: string;
  createdBy: { memberId: string; name: string };
  arrivals: { memberId: string; name: string; arrivedAt: string }[];
  iArrived: boolean;
  canClose: boolean;
  closedAt: string | null;
}

export interface Aviso {
  id: string;
  text: string;
  createdBy: { memberId: string; name: string };
  createdAt: string;
  expiresAt: string;
}

async function pedir<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { credentials: 'same-origin', ...init, headers: { ...(init?.body ? { 'content-type': 'application/json' } : {}), ...(init?.headers ?? {}) } });
  if (!res.ok) {
    const corpo = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(corpo.message ?? `Falha (${res.status})`);
  }
  return (await res.json()) as T;
}

export const encontroApi = {
  atual: (tripId: string) => pedir<{ meeting: Encontro | null }>(`/api/v1/trips/${tripId}/meeting`),
  criar: (tripId: string, input: { id: string; title: string; latitude: number; longitude: number; at: string }) =>
    pedir<{ meeting: Encontro }>(`/api/v1/trips/${tripId}/meeting`, { method: 'POST', body: JSON.stringify(input) }),
  cheguei: (tripId: string, meetingId: string) => pedir<{ meeting: Encontro }>(`/api/v1/trips/${tripId}/meeting/${meetingId}/arrive`, { method: 'POST' }),
  encerrar: (tripId: string, meetingId: string) => pedir<{ ok: true }>(`/api/v1/trips/${tripId}/meeting/${meetingId}/close`, { method: 'POST' }),
  aviso: (tripId: string) => pedir<{ notice: Aviso | null }>(`/api/v1/trips/${tripId}/notice`),
  avisar: (tripId: string, input: { id: string; text: string }) => pedir<{ notice: Aviso }>(`/api/v1/trips/${tripId}/notice`, { method: 'POST', body: JSON.stringify(input) }),
};

/** O ponto atual — relê a cada 20 s enquanto a tela está aberta. */
export function useEncontro(tripId: string, ligado = true) {
  return useQuery({ queryKey: ['encontro', tripId], queryFn: () => encontroApi.atual(tripId), enabled: ligado, refetchInterval: ligado ? 20_000 : false, staleTime: 10_000 });
}

export function useAviso(tripId: string, ligado = true) {
  return useQuery({ queryKey: ['aviso', tripId], queryFn: () => encontroApi.aviso(tripId), enabled: ligado, refetchInterval: ligado ? 30_000 : false, staleTime: 15_000 });
}

export function useInvalidarEncontro(tripId: string) {
  const cliente = useQueryClient();
  useEffect(() => {
    const ao = () => {
      void cliente.invalidateQueries({ queryKey: ['encontro', tripId] });
      void cliente.invalidateQueries({ queryKey: ['aviso', tripId] });
    };
    window.addEventListener('nv:encontro-mudou', ao);
    return () => window.removeEventListener('nv:encontro-mudou', ao);
  }, [cliente, tripId]);
  return () => window.dispatchEvent(new Event('nv:encontro-mudou'));
}
